// The printed "Cash wheat targets" page. Scoped like the election sheet (`cwt-`), so the
// report's own CSS cannot reach it and it cannot reach the report's.
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const d2 = (v) => (v == null ? "—" : "$" + Number(v).toFixed(2));
const bas = (v) => (v == null ? "—" : (Number(v) >= 0 ? "+" : "") + Number(v).toFixed(2));
const pct = (v) => (v == null ? "—" : Math.round(Number(v) * 100) + "%");
const MON = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// ⚠ THE BOARD DOES NOT STAMP ITS SHEET IN ISO. `sheet_updated` arrives as "9/15/2026 16:08",
// so an ISO-only formatter returned null and the page printed the raw timestamp, clock and all,
// in the middle of a provenance sentence. Both shapes format; anything else prints as given
// rather than as "null".
const dateStr = (v) => {
  const s = String(v || "").trim(); if (!s) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return MON[+iso[2]] + " " + +iso[3] + ", " + iso[1];
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s);
  if (us) return MON[+us[1]] + " " + +us[2] + ", " + us[3];
  return s;
};
const TERM_LABEL = { delivered: "Delivered", pickup: "Picked up", unstated: "Term not posted" };

const CSS = `
.cwt { font: 13px/1.4 -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1f2a1f; padding: 2px 0 0; }
.cwt h2 { margin: 0 0 2px; font-size: 21px; letter-spacing: -.01em; }
.cwt .cwt-sub { font-size: 12px; color: #5c6f5c; margin-bottom: 10px; }
.cwt table { width: 100%; border-collapse: collapse; margin-top: 4px; }
.cwt th { text-align: left; font-size: 10px; letter-spacing: .04em; text-transform: uppercase; color: #6b7a6b; border-bottom: 1px solid #d5e2d5; padding: 0 8px 3px 0; font-weight: 700; }
.cwt td { padding: 6px 8px 6px 0; border-bottom: 1px solid #eef3ee; vertical-align: top; }
.cwt .cwt-lv { font-weight: 800; font-size: 15px; white-space: nowrap; }
.cwt .cwt-tag { font-size: 11px; color: #5c6f5c; text-transform: capitalize; }
.cwt .cwt-cash { font-weight: 800; white-space: nowrap; }
.cwt .cwt-pts { font-size: 11px; color: #6b7a6b; }
.cwt .cwt-note { margin-top: 10px; font-size: 11px; line-height: 1.45; color: #6b7a6b; }
.cwt .cwt-absent { margin-top: 8px; padding: 8px 10px; border: 1px solid #e6dcc0; background: #fbf7ec; font-size: 12px; color: #6b5a2a; }
`;

export function cashTargetsSection(t, { farmName = null } = {}) {
  if (!t) return "";
  const head = `<h2>Cash wheat targets${t.contractLabel ? " · " + esc(t.contractLabel) : ""}</h2>
    <div class="cwt-sub">Where the market has historically traded, in cash — a reference ladder, not an instruction to sell.${
      t.boardLast != null ? " Board last settled <b>" + d2(t.boardLast) + "</b>" + (t.asOf ? " on " + esc(dateStr(t.asOf)) : "") + "." : ""}</div>`;

  if (!t.ok) {
    const why = t.absent === "no posted basis for this delivery month"
      ? "No basis is posted for this delivery month, so these are <b>board levels, not cash</b> — add your own basis to read them as cash."
      : "No measured target ladder for this board.";
    const rows = (t.rows || []).map((r) => `<tr><td class="cwt-lv">${d2(r.futures)}</td>
      <td class="cwt-tag">${esc(r.label || "")}</td>
      <td>${r.fill != null ? "reached " + pct(r.fill) + " of the time" : "—"}</td></tr>`).join("");
    return `<style>${CSS}</style><section class="page cwt">${head}
      <div class="cwt-absent">${why}</div>
      ${rows ? `<table><thead><tr><th>Board level</th><th></th><th>History</th></tr></thead><tbody>${rows}</tbody></table>` : ""}
      ${NOTE(t)}</section>`;
  }

  const terms = t.terms || [];
  const body = (t.rows || []).map((r) => {
    const cells = terms.map((g) => {
      const c = (r.cash || []).find((x) => x.term === g.term);
      if (!c) return "<td>—</td>";
      const same = c.low === c.high;
      return `<td><span class="cwt-cash">${same ? d2(c.low) : d2(c.low) + "–" + d2(c.high)}</span>
        <div class="cwt-pts">basis ${same ? bas(c.basisLow) : bas(c.basisLow) + " to " + bas(c.basisHigh)}</div></td>`;
    }).join("");
    // ⚠ THE SUBJECTIVE TIER IS GONE (Kevin, 2026-09-22: "Remove subjective labels
    // such as Attainable / Strong / Flyer"). "Attainable" and "Flyer" are verdicts
    // about a price, and the reach rate in the last column already says the same
    // thing as a measured number. What stays is the LEVEL the aim was snapped to —
    // a 1-month high, a Fibonacci extension, a round number — which is a fact about
    // where the market has traded and answers "why that price and not a round guess".
    return `<tr><td class="cwt-lv">${d2(r.futures)}${
      r.level ? '<div class="cwt-tag" style="text-transform:none;color:#8a988a">' + esc(r.level) + "</div>" : ""}</td>
      ${cells}<td>${r.fill != null ? "reached <b>" + pct(r.fill) + "</b> of the time" : "—"}</td></tr>`;
  }).join("");

  const ths = terms.map((g) => `<th>${esc(TERM_LABEL[g.term] || g.term)}<div class="cwt-pts" style="text-transform:none;font-weight:400">${g.points.length} point${g.points.length === 1 ? "" : "s"} posted</div></th>`).join("");
  return `<style>${CSS}</style><section class="page cwt">${head}
    <table><thead><tr><th>Board level</th>${ths}<th>How often the board reached it</th></tr></thead>
    <tbody>${body}</tbody></table>
    ${NOTE(t)}</section>`;
}

function NOTE(t) {
  const pts = (t.terms || []).flatMap((g) => (g.points || []).map((p) => p.location)).filter(Boolean);
  const uniq = [...new Set(pts)];
  return `<div class="cwt-note">
    Board levels are measured, not chosen: each is the distance this board's own last five years of
    trading cleared that often within the time it has left to work, snapped to a level the market
    already respects. <b>Cash is the board level plus the basis posted</b>${
      t.sheetVersion ? " on sheet " + esc(t.sheetVersion) : ""}${
      t.sheetUpdated ? ", " + esc(dateStr(t.sheetUpdated) || t.sheetUpdated) : ""}${
      uniq.length ? " for " + esc(uniq.join(" · ")) : ""}.
    Delivered and picked up are shown separately because the difference between them is freight,
    which this report does not know. Posted points are listed as the sheet posts them — this is not
    a recommendation of where to sell, and a basis moves until it is contracted.
    History is not a forecast.</div>`;
}
